/**
 * Feature flags, per device.
 *
 * Some of what this app does carries a real risk to the user, and the risk is
 * not the same kind in each case:
 *
 *   - **Sharing** moves figures OUT of the encrypted envelope. Everything else
 *     in this product is built on the promise that the books exist in exactly
 *     two decrypted places; a share sheet is the one feature that breaks it, on
 *     purpose, at the user's request.
 *   - **The app lock** can lock a user out of their own books. The snapshot is
 *     recoverable (re-download, re-enter the passphrase) but the panic is real.
 *   - **Alerts** cost a permission prompt and, done badly, become nagging.
 *
 * So each one is a switch the user owns, defaulting to off, and each switch is
 * checked at the point of use rather than hidden behind a build flag. A feature
 * you cannot turn off is a feature you cannot safely test on your own company.
 *
 * Flags are per device, not per company: they describe how THIS phone behaves.
 * They are stored in IndexedDB settings alongside the connection, never in
 * localStorage — that store is reserved for nothing at all here (CLAUDE.md §10).
 */
import { getSetting, setSetting } from "./sync/db.js";

export type FeatureId = "share" | "appLock" | "alerts" | "advancedReports";

export type Feature = {
  id: FeatureId;
  title: string;
  /** what it does, in the user's terms */
  blurb: string;
  /** the honest downside — shown next to the switch, not buried */
  risk: string;
  default: boolean;
};

export const FEATURES: Feature[] = [
  {
    id: "share",
    title: "Share and export",
    blurb:
      "Send a party's statement or an outstanding list over WhatsApp, email or " +
      "anything else on your phone. Also exports any report as a CSV file.",
    risk:
      "Anything you share leaves the encrypted copy and lands in that app's " +
      "storage, backups and forwards. Nothing else in Ledgerly Go does this.",
    default: false,
  },
  {
    id: "appLock",
    title: "Lock this app",
    blurb:
      "Ask for a PIN — or your fingerprint, where the phone supports it — before " +
      "showing any figures, instead of re-typing the passphrase every time.",
    risk:
      "Forgetting the PIN means unlocking with the passphrase again. Your data " +
      "is never lost, but the passphrase is the only way back in.",
    default: false,
  },
  {
    id: "alerts",
    title: "Alerts",
    blurb:
      "Tell you when a bill falls overdue, or when the figures on this phone " +
      "have gone stale because the laptop has not synced.",
    risk:
      "Needs notification permission. Alerts only fire while the app is open — " +
      "there is no server pushing to your phone, by design.",
    default: false,
  },
  {
    id: "advancedReports",
    title: "Cost centres, godowns and batches",
    blurb:
      "Extra screens for cost-centre allocation, godown-wise stock and batch " +
      "expiry, for books that use them.",
    risk:
      "Only meaningful if these are set up in Tally. On books that do not use " +
      "them the screens are simply empty.",
    default: false,
  },
];

export type Flags = Record<FeatureId, boolean>;

export const DEFAULT_FLAGS: Flags = Object.fromEntries(
  FEATURES.map((f) => [f.id, f.default]),
) as Flags;

const settingKey = (id: FeatureId) => `feature:${id}`;

export async function loadFlags(): Promise<Flags> {
  const entries = await Promise.all(
    FEATURES.map(async (f) => [f.id, await getSetting(settingKey(f.id), f.default)] as const),
  );
  return Object.fromEntries(entries) as Flags;
}

export async function saveFlag(id: FeatureId, on: boolean): Promise<void> {
  await setSetting(settingKey(id), on);
}
